"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { shown } from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type {
  RIEMANN_SUM_CALCULATOR_STRINGS,
  RiemannView,
} from "@/components/tools/riemann-sum-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { autoWindow } from "@/lib/tool-kit/calculus/plot";
import {
  convergenceTable,
  effectiveN,
  type RiemannMethod,
  riemann,
  riemannSum,
} from "@/lib/tool-kit/calculus/riemann";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof RIEMANN_SUM_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<RiemannView, { kind: "ok" }>;

const ALL_METHODS: readonly RiemannMethod[] = [
  "left",
  "right",
  "midpoint",
  "trapezoid",
  "simpson",
];
const COUNTS = [4, 8, 16, 32, 64, 128];
const MAX_TABLE_ROWS = 24;
const HEAD_ROWS = 14;
const TAIL_ROWS = 6;
const FILL_OPACITY = 0.28;

function num(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0 ? fmt(0) : fmt(value, { maximumSignificantDigits: 10 });
}

function errorText(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  // Below 10⁻¹² is rounding in the last digit of a sum, not error of the rule.
  return Math.abs(value) < 1e-12
    ? fmt(0)
    : fmt(value, { maximumSignificantDigits: 4, signDisplay: "exceptZero" });
}

interface ShapeInput {
  method: RiemannMethod;
  f: (x: number) => number;
  a: number;
  b: number;
  n: number;
  scales: PlaneScales;
}

function simpsonArc(
  f: (x: number) => number,
  x0: number,
  x2: number,
  scales: PlaneScales
): string {
  const x1 = (x0 + x2) / 2;
  const [y0, y1, y2] = [f(x0), f(x1), f(x2)];
  const axis = scales.y(0);
  const points: string[] = [];
  for (let i = 0; i <= 24; i += 1) {
    const u = (2 * i) / 24 - 1;
    // Lagrange form of the parabola through the three samples, in u = (x − x₁)/h.
    const y = y1 + ((y2 - y0) / 2) * u + ((y0 - 2 * y1 + y2) / 2) * u * u;
    points.push(
      `L${scales.x(x1 + u * (x2 - x1)).toFixed(1)} ${scales.y(y).toFixed(1)}`
    );
  }
  return `M${scales.x(x0).toFixed(1)} ${axis.toFixed(1)} ${points.join(" ")} L${scales.x(x2).toFixed(1)} ${axis.toFixed(1)} Z`;
}

function Shapes({ method, f, a, b, n, scales }: ShapeInput) {
  const h = (b - a) / n;
  const axis = scales.y(0);
  const stroke = n <= 120 ? CHART_COLORS[1] : "none";
  if (method === "simpson") {
    return (
      <>
        {Array.from({ length: n / 2 }, (_, k) => {
          const x0 = a + 2 * k * h;
          return (
            <path
              d={simpsonArc(f, x0, x0 + 2 * h, scales)}
              fill={CHART_COLORS[1]}
              fillOpacity={FILL_OPACITY}
              key={x0}
              stroke={stroke}
            />
          );
        })}
      </>
    );
  }
  return (
    <>
      {Array.from({ length: n }, (_, i) => {
        const x0 = a + i * h;
        const x1 = x0 + h;
        const left = scales.x(x0);
        const right = scales.x(x1);
        if (method === "trapezoid") {
          const d = `M${left} ${axis} L${left} ${scales.y(f(x0))} L${right} ${scales.y(f(x1))} L${right} ${axis} Z`;
          return (
            <path
              d={d}
              fill={CHART_COLORS[1]}
              fillOpacity={FILL_OPACITY}
              key={x0}
              stroke={stroke}
            />
          );
        }
        let sample = x0;
        if (method === "right") {
          sample = x1;
        } else if (method === "midpoint") {
          sample = x0 + h / 2;
        }
        const top = scales.y(f(sample));
        return (
          <rect
            fill={CHART_COLORS[1]}
            fillOpacity={FILL_OPACITY}
            height={Math.abs(axis - top)}
            key={x0}
            stroke={stroke}
            width={right - left}
            x={left}
            y={Math.min(axis, top)}
          />
        );
      })}
    </>
  );
}

function sampleRows(
  rows: { index: number; x: number; fx: number; weight: number }[],
  method: RiemannMethod,
  fmt: Fmt,
  strings: Strings
): string[][] {
  const showWeight = method === "trapezoid" || method === "simpson";
  const toRow = (row: (typeof rows)[number]) => {
    const base = [String(row.index), num(fmt, row.x), num(fmt, row.fx)];
    return showWeight
      ? [...base, String(row.weight), num(fmt, row.weight * row.fx)]
      : base;
  };
  if (rows.length <= MAX_TABLE_ROWS) {
    return rows.map(toRow);
  }
  const gap = Array.from({ length: showWeight ? 5 : 3 }, () => strings.skipped);
  return [
    ...rows.slice(0, HEAD_ROWS).map(toRow),
    gap,
    ...rows.slice(-TAIL_ROWS).map(toRow),
  ];
}

export function RiemannResults({
  view,
  strings,
  fmt,
}: {
  view: OkView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { f, method, lower, upper, decimals } = view;
  const a = lower.value;
  const b = upper.value;
  const result = useMemo(
    () => riemann(f, method, a, b, view.requested),
    [f, method, a, b, view.requested]
  );
  const window = useMemo(() => {
    const base = autoWindow([f], a - (b - a) * 0.05, b + (b - a) * 0.05);
    const pad = (base.yMax - base.yMin) * 0.05;
    return {
      ...base,
      yMin: Math.min(base.yMin, -pad),
      yMax: Math.max(base.yMax, pad),
    };
  }, [f, a, b]);
  if (result.kind === "undefined") {
    return (
      <ErrorNotice>
        {t(strings.undefinedAt, {
          at: fmt(result.at, { maximumSignificantDigits: 6 }),
        })}
      </ErrorNotice>
    );
  }
  const rule = strings.methods[method];
  const weighted = result.rows.reduce(
    (sum, row) => sum + row.weight * row.fx,
    0
  );
  const showWeight = method === "trapezoid" || method === "simpson";
  const head = [
    strings.columnIndex,
    method === "midpoint" ? strings.columnMidpoint : strings.columnX,
    strings.columnFx,
    ...(showWeight ? [strings.columnWeight, strings.columnTerm] : []),
  ];
  const exact = result.exact;
  const compare = ALL_METHODS.map((m) => {
    const n = effectiveN(m, result.n);
    const value = riemannSum(f, m, a, b, n);
    return [
      strings.methods[m],
      String(n),
      num(fmt, value),
      errorText(fmt, value - exact),
    ];
  });
  const convergence = convergenceTable(f, method, a, b, exact, COUNTS).map(
    (row) => [
      String(effectiveN(method, row.n)),
      num(fmt, row.sum),
      errorText(fmt, row.error),
    ]
  );
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.approximationLabel}
          value={num(fmt, result.sum)}
        />
        <StatCard
          label={strings.exactLabel}
          value={Number.isFinite(exact) ? num(fmt, exact) : strings.noExact}
        />
        <StatCard
          label={strings.errorLabel}
          value={errorText(fmt, result.error)}
        />
        <StatCard label={strings.widthLabel} value={num(fmt, result.h)} />
      </StatGrid>
      {result.n !== result.requested ? (
        <Notice>
          {t(strings.evenNote, { requested: result.requested, n: result.n })}
        </Notice>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
        <p className="text-foreground text-sm">
          {t(strings.formulas[method], { h: num(fmt, result.h) })}
        </p>
        <p className="font-mono text-foreground text-sm">
          {t(strings.sumLine, {
            factor: num(fmt, result.factor),
            weighted: num(fmt, weighted),
            sum: num(fmt, result.sum),
          })}
        </p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <MathTable
          head={head}
          rows={sampleRows(result.rows, method, fmt, strings)}
        />
        {result.rows.length > MAX_TABLE_ROWS ? (
          <p className="text-muted-foreground text-sm">
            {t(strings.tableNote, { count: result.rows.length })}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.compareHeading, { n: result.n })}
        </h3>
        <MathTable
          head={[
            strings.columnRule,
            strings.columnN,
            strings.columnValue,
            strings.columnError,
          ]}
          rows={compare}
        />
      </div>
      {Number.isFinite(exact) ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>
            {t(strings.convergenceHeading, { rule })}
          </h3>
          <MathTable
            head={[strings.columnN, strings.columnValue, strings.columnError]}
            rows={convergence}
          />
          <p className="text-muted-foreground text-sm">
            {strings.convergenceNote}
          </p>
        </div>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="riemann-sum-graph"
          series={[
            { f, color: CHART_COLORS[0], label: strings.seriesFunction },
            {
              f: () => Number.NaN,
              color: CHART_COLORS[1],
              width: 8,
              label: t(strings.seriesShapes, { rule, n: result.n }),
            },
          ]}
          title={t(strings.graphTitle, {
            rule,
            lower: shown(lower.ast, decimals),
            upper: shown(upper.ast, decimals),
          })}
          under={(scales) => (
            <Shapes
              a={a}
              b={b}
              f={f}
              method={method}
              n={result.n}
              scales={scales}
            />
          )}
          window={window}
        />
      </div>
    </>
  );
}
